import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Cpu, Loader2, Lock, User, CheckCircle2, AlertCircle, Zap } from "lucide-react";

const OdooConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [authData, setAuthData] = useState({
    username: "",
    password: "",
  });

  const handleChange = (e) => {
    setAuthData({ ...authData, [e.target.name]: e.target.value });
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Hardcoded fields per your requirement
    const payload = {
      crm: "odoo",
      url: "http://localhost:8069/jsonrpc",
      db: "Test",
      username: authData.username,
      password: authData.password,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_AI_BACKEND_URL}/crm/auth-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log("response",response)

      const data = await response.json();
      console.log("data",data)

      if (data.ok) {
        setSuccess(true);
        // Store schema in local storage if needed for the chatbot
        localStorage.setItem("crm_schema", JSON.stringify(data.schema_sample));
        
        setTimeout(() => {
          navigate("/dashboard/crm-ai/odoo/chatbot");
        }, 1500);
      } else {
        setError("Authentication failed. Please check your Odoo credentials.");
      }
    } catch (err) {
      setError("Unable to reach the server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#01040a] flex items-center justify-center p-6 relative overflow-hidden font-sans">
      
      {/* --- FLASHY BACKGROUND EFFECTS --- */}
      <style>{`
        @keyframes driftUp {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { transform: translateY(-100vh); opacity: 0; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(34, 211, 238, 0.2); }
          50% { box-shadow: 0 0 40px rgba(34, 211, 238, 0.5); }
        }
      `}</style>

      {/* 1. Neural Mesh */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle, #22d3ee 1px, transparent 1.5px)`,
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(circle at center, black, transparent 90%)'
        }}
      />

      {/* 2. Floating High-Vis Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-cyan-400 rounded-full shadow-[0_0_10px_#22d3ee]"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animation: `driftUp ${Math.random() * 10 + 5}s linear infinite`,
              animationDelay: `-${Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* 3. Deep Ambient Glows */}
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-cyan-500/10 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-blue-600/10 blur-[120px] rounded-full" />

      {/* --- MAIN CARD --- */}
      <div className="max-w-md w-full relative z-10 animate-in fade-in zoom-in duration-700">
        <div className="bg-[#0f172a]/90 backdrop-blur-3xl rounded-[3rem] border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.7)] overflow-hidden">
          
          {/* Brand Header */}
          <div className="relative pt-12 pb-8 px-10 text-center">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
            
            <div className="w-20 h-20 bg-gradient-to-br from-[#0a121a] to-[#1e293b] border-2 border-cyan-400/50 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(34,211,238,0.3)] animate-[glowPulse_3s_infinite]">
              <span className="text-5xl font-black text-cyan-400">O</span>
            </div>
            
            <h2 className="text-3xl font-black text-white tracking-tight uppercase italic">Odoo Enterprise</h2>
            <div className="flex items-center justify-center gap-2 mt-2">
               <span className="h-[1px] w-8 bg-cyan-500/30"></span>
               <p className="text-cyan-400/80 text-[10px] font-black uppercase tracking-[0.3em]">AI Bridge Protocol</p>
               <span className="h-[1px] w-8 bg-cyan-500/30"></span>
            </div>
          </div>

          <div className="px-10 pb-12 pt-4">
            {!success ? (
              <form onSubmit={handleConnect} className="space-y-6">
                
                {/* Username */}
                <div className="group">
                  <label className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 ml-2 group-focus-within:text-cyan-400 transition-colors">
                    <User size={12} />
                    Odoo Identity / Email
                  </label>
                  <div className="relative">
                    <input
                      name="username"
                      type="text"
                      required
                      value={authData.username}
                      onChange={handleChange}
                      placeholder="e.g. admin@company.com"
                      className="w-full px-6 py-5 rounded-2xl bg-[#01040a]/50 border border-white/5 focus:border-cyan-400/50 focus:ring-4 focus:ring-cyan-400/5 outline-none transition-all text-white placeholder:text-slate-700"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="group">
                  <label className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 ml-2 group-focus-within:text-cyan-400 transition-colors">
                    <Lock size={12} />
                    System Password
                  </label>
                  <input
                    name="password"
                    type="password"
                    required
                    value={authData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full px-6 py-5 rounded-2xl bg-[#01040a]/50 border border-white/5 focus:border-cyan-400/50 focus:ring-4 focus:ring-cyan-400/5 outline-none transition-all text-white placeholder:text-slate-700"
                  />
                </div>

                {/* Error Notification */}
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold rounded-xl animate-bounce">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full py-5 bg-cyan-500 text-[#01040a] rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all hover:bg-cyan-400 hover:scale-[1.02] active:scale-[0.98] shadow-[0_10px_30px_rgba(34,211,238,0.3)] disabled:bg-slate-800 disabled:text-slate-600 overflow-hidden"
                >
                  <div className="absolute inset-0 w-1/2 h-full bg-white/20 skew-x-[-25deg] -translate-x-full group-hover:animate-[shimmer_1s_infinite]" />
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <Loader2 className="animate-spin" size={18} />
                      Syncing Nodes...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Zap size={16} fill="currentColor" />
                      Initialize Link
                    </span>
                  )}
                </button>
              </form>
            ) : (
              /* Success Redirection State */
              <div className="text-center py-6">
                <div className="relative inline-block mb-6">
                  <div className="absolute inset-0 bg-cyan-400 blur-2xl opacity-40 animate-pulse" />
                  <div className="relative w-24 h-24 bg-cyan-400 rounded-full flex items-center justify-center border-4 border-[#01040a] shadow-2xl">
                    <CheckCircle2 className="w-12 h-12 text-[#01040a]" />
                  </div>
                </div>
                <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter">Authorized</h3>
                <p className="text-slate-400 mt-2 font-light text-sm italic">Accessing Chatbot Interface...</p>
              </div>
            )}
          </div>

          {/* Footer Info */}
          <div className="pb-10 px-10">
             <div className="flex items-center justify-center gap-3 py-4 bg-white/5 rounded-2xl border border-white/5">
                <ShieldCheck size={16} className="text-cyan-400" />
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">JSON-RPC Tunnel Secured</span>
             </div>
          </div>
        </div>
      </div>
      
      {/* CSS Shimmer Effect */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(250%); }
        }
      `}</style>

    </div>
  );
};

export default OdooConfig;